import React, { useState, useEffect, useCallback } from 'react';

const StepRevenusMensuels = ({ formData, onChange }) => {
  // État local pour gérer l'affichage des revenus supplémentaires
  const [showAutresRevenus, setShowAutresRevenus] = useState(formData.autresRevenus === true);
  // État pour les messages d'erreur
  const [fieldErrors, setFieldErrors] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // Gestionnaire pour le changement des revenus supplémentaires
  const handleAutresRevenusChange = (value) => {
    setShowAutresRevenus(value);
    onChange({ target: { name: 'autresRevenus', value } });
    
    // Effacer l'erreur quand une sélection est faite
    clearFieldError('autresRevenus');
  };

  // Fonction pour effacer une erreur spécifique
  const clearFieldError = (fieldName) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  // Gestionnaire pour les changements de champ
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({ target: { name, value } });
    clearFieldError(name);
  };

  // Validation des champs - utilisation de useCallback pour éviter les recreations
  const validateFields = useCallback(() => {
    const errors = {};
    let isValid = true;

    // Validation du salaire net mensuel
    if (!formData.revenusNets || isNaN(parseFloat(formData.revenusNets)) || parseFloat(formData.revenusNets) <= 0) {
      errors.revenusNets = 'Veuillez indiquer un montant valide pour vos revenus mensuels nets';
      isValid = false;
    }

    // Validation de la sélection d'avoir ou non des revenus supplémentaires
    if (formData.autresRevenus !== true && formData.autresRevenus !== false) {
      errors.autresRevenus = 'Veuillez indiquer si vous avez des revenus supplémentaires';
      isValid = false;
    }

    // Si des revenus supplémentaires sont indiqués, vérifier que les montants sont valides
    if (formData.autresRevenus === true) {
      // On ne rend pas ces champs obligatoires mais on vérifie qu'ils sont des nombres valides s'ils sont renseignés
      if (formData.pensionAlimentaire && (isNaN(parseFloat(formData.pensionAlimentaire)) || parseFloat(formData.pensionAlimentaire) < 0)) {
        errors.pensionAlimentaire = 'Veuillez indiquer un montant valide';
        isValid = false;
      }
      
      if (formData.allocationsCaf && (isNaN(parseFloat(formData.allocationsCaf)) || parseFloat(formData.allocationsCaf) < 0)) {
        errors.allocationsCaf = 'Veuillez indiquer un montant valide';
        isValid = false;
      }
      
      if (formData.revenusFonciers && (isNaN(parseFloat(formData.revenusFonciers)) || parseFloat(formData.revenusFonciers) < 0)) {
        errors.revenusFonciers = 'Veuillez indiquer un montant valide';
        isValid = false;
      }
      
      if (formData.autresRevenusDescription && (isNaN(parseFloat(formData.autresRevenusDescription)) || parseFloat(formData.autresRevenusDescription) < 0)) {
        errors.autresRevenusDescription = 'Veuillez indiquer un montant valide';
        isValid = false;
      }
      
      // Si "autres revenus" est renseigné avec un montant > 0, la nature doit être précisée
      if (formData.autresRevenusDescription && parseFloat(formData.autresRevenusDescription) > 0 && !formData.autresRevenusNature) {
        errors.autresRevenusNature = 'Veuillez préciser la nature de ces revenus';
        isValid = false;
      }
    }

    setFieldErrors(errors);
    return isValid;
  }, [formData]); // Dépendance à formData pour recalculer lorsque les données changent

  // Intercepter le clic sur le bouton Suivant
  useEffect(() => {
    const nextButton = document.querySelector('button.bg-orpi-primary:not(.mt-8)');
    if (nextButton) {
      const originalOnClick = nextButton.onclick;
      
      nextButton.onclick = (e) => {
        setAttemptedSubmit(true);
        const isValid = validateFields();
        
        if (!isValid) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        
        // Si valide, continuer avec le comportement normal
        if (originalOnClick) {
          return originalOnClick(e);
        }
      };
      
      // Nettoyage
      return () => {
        if (nextButton) {
          nextButton.onclick = originalOnClick;
        }
      };
    }
  }, [formData, validateFields]); // Ajout de validateFields comme dépendance

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-orpi-secondary">
        Vos revenus mensuels
      </h2>

      {attemptedSubmit && Object.keys(fieldErrors).length > 0 && (
        <div className="bg-red-100 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p className="font-medium">Veuillez corriger les erreurs suivantes :</p>
          <ul className="list-disc pl-5 mt-1">
            {Object.values(fieldErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Salaire net mensuel avant impôt <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              name="revenusNets"
              value={formData.revenusNets || ''}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg pr-12 focus:border-orpi-primary focus:ring-orpi-primary ${
                fieldErrors.revenusNets ? 'border-red-500' : ''
              }`}
              placeholder="0"
              min="0"
              required
            />
            <span className="absolute right-3 top-3 text-gray-500">€</span>
          </div>
          {fieldErrors.revenusNets && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.revenusNets}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Nombre de mois de versement par an
          </label>
          <select
            name="nbMoisVersement"
            value={formData.nbMoisVersement || '12'}
            onChange={handleChange}
            className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
            required
          >
            <option value="12">12 mois</option>
            <option value="13">13 mois</option>
            <option value="14">14 mois</option>
            <option value="15">15 mois</option>
            <option value="16">16 mois</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Avez-vous des revenus supplémentaires ? <span className="text-red-500">*</span>
          </label>
          <div className={`grid grid-cols-2 gap-4 mb-3 ${fieldErrors.autresRevenus ? 'border border-red-200 p-2 rounded' : ''}`}>
            <button
              type="button"
              className={`p-3 border rounded-lg hover:border-orpi-primary ${
                formData.autresRevenus === true ? 'border-orpi-primary bg-orpi-light' : ''
              }`}
              onClick={() => handleAutresRevenusChange(true)}
            >
              Oui
            </button>
            <button
              type="button"
              className={`p-3 border rounded-lg hover:border-orpi-primary ${
                formData.autresRevenus === false ? 'border-orpi-primary bg-orpi-light' : ''
              }`}
              onClick={() => handleAutresRevenusChange(false)}
            >
              Non
            </button>
          </div>
          {fieldErrors.autresRevenus && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.autresRevenus}</p>
          )}
          <p className="text-sm text-gray-500">
            Pension, allocation familiale, aides au logement, etc.
          </p>
        </div>

        {showAutresRevenus && (
          <div className="space-y-4 border-t pt-4">
            <p className="text-sm font-medium text-orpi-secondary">Indiquez ci-dessous vos revenus supplémentaires :</p>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">Pensions</label>
              <div className="relative">
                <input
                  type="number"
                  name="pensionAlimentaire"
                  value={formData.pensionAlimentaire || ''}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg pr-12 focus:border-orpi-primary focus:ring-orpi-primary ${
                    fieldErrors.pensionAlimentaire ? 'border-red-500' : ''
                  }`}
                  placeholder="0"
                  min="0"
                />
                <span className="absolute right-3 top-3 text-gray-500">€</span>
              </div>
              {fieldErrors.pensionAlimentaire && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.pensionAlimentaire}</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Allocations familiales</label>
              <div className="relative">
                <input
                  type="number"
                  name="allocationsCaf"
                  value={formData.allocationsCaf || ''}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg pr-12 focus:border-orpi-primary focus:ring-orpi-primary ${
                    fieldErrors.allocationsCaf ? 'border-red-500' : ''
                  }`}
                  placeholder="0"
                  min="0"
                />
                <span className="absolute right-3 top-3 text-gray-500">€</span>
              </div>
              {fieldErrors.allocationsCaf && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.allocationsCaf}</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Revenus fonciers (loyers perçus)</label>
              <div className="relative">
                <input
                  type="number"
                  name="revenusFonciers"
                  value={formData.revenusFonciers || ''}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg pr-12 focus:border-orpi-primary focus:ring-orpi-primary ${
                    fieldErrors.revenusFonciers ? 'border-red-500' : ''
                  }`}
                  placeholder="0"
                  min="0"
                />
                <span className="absolute right-3 top-3 text-gray-500">€</span>
              </div>
              {fieldErrors.revenusFonciers && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.revenusFonciers}</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Autres revenus</label>
              <div className="relative">
                <input
                  type="number"
                  name="autresRevenusDescription"
                  value={formData.autresRevenusDescription || ''}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg pr-12 focus:border-orpi-primary focus:ring-orpi-primary ${
                    fieldErrors.autresRevenusDescription ? 'border-red-500' : ''
                  }`}
                  placeholder="0"
                  min="0"
                />
                <span className="absolute right-3 top-3 text-gray-500">€</span>
              </div>
              {fieldErrors.autresRevenusDescription && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.autresRevenusDescription}</p>
              )}
            </div>

            {(formData.autresRevenusDescription && parseFloat(formData.autresRevenusDescription) > 0) && (
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Nature des autres revenus <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="autresRevenusNature"
                  value={formData.autresRevenusNature || ''}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary ${
                    fieldErrors.autresRevenusNature ? 'border-red-500' : ''
                  }`}
                  placeholder="Ex: Rentes, indemnités, etc."
                />
                {fieldErrors.autresRevenusNature && (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.autresRevenusNature}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      <p className="text-sm text-gray-500 mt-6">
        <span className="text-red-500">*</span> Champs obligatoires
      </p>
    </div>
  );
};

export default StepRevenusMensuels;